/**
 * Screenshot capture, annotation rendering and size-bounded encoding.
 *
 * The invariant this file exists for: **only the annotated bitmap ever leaves
 * the browser**. Redactions are painted onto the pixels before encoding, so
 * there is no original to leak — not in the upload, not in storage.
 */

export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
const MAX_EDGE_PX = 1600;

// Annotations are measured against the CAPTURE, not in absolute pixels. A
// retina screenshot is ~2600px wide, and a fixed 18px caption on one of those
// is unreadable — which is exactly what the first version shipped.
export const TEXT_LINE_HEIGHT = 1.35;
const TEXT_PADDING_RATIO = 0.55; // of the font size
const TEXT_MIN_WIDTH_RATIO = 0.18; // of the region width
const TEXT_GAP_RATIO = 0.012; // of the region height
const STROKE_RATIO = 0.004; // of the rendered height
const FONT_RATIO = 0.024; // of the capture height

/** Caption size for a capture, in SOURCE pixels. */
export function textFontSizeFor(source) {
  const height = source?.height || 900;
  return Math.round(Math.min(72, Math.max(18, height * FONT_RATIO)));
}

export const textPadding = (fontPx) => Math.round(fontPx * TEXT_PADDING_RATIO);
export const TEXT_FONT_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Types the backend accepts, best first. */
const ENCODE_ATTEMPTS = [
  { type: 'image/webp', quality: 0.85 },
  { type: 'image/webp', quality: 0.6 },
  { type: 'image/jpeg', quality: 0.75 },
  { type: 'image/png' },
];

export class CaptureCancelled extends Error {}
export class CaptureUnsupported extends Error {}

export function isDisplayCaptureSupported() {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * Grab exactly one frame from the browser's own screen-capture picker.
 *
 * The browser owns the permission prompt and the source choice — we only state
 * a preference for the current tab. Every track is stopped before this
 * resolves or rejects, on all three paths (frame captured, user cancelled,
 * capture failed): a live capture indicator left running after a one-shot
 * screenshot is alarming and, on some platforms, keeps the tab marked as
 * being recorded.
 */
export async function captureViewportFrame() {
  if (!isDisplayCaptureSupported()) throw new CaptureUnsupported();

  let stream = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      // `preferCurrentTab` and `selfBrowserSurface` are Chromium-only hints;
      // other engines ignore unknown members here rather than throwing.
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      systemAudio: 'exclude',
    });
    return await grabFrame(stream);
  } catch (error) {
    // Every engine reports a dismissed picker as NotAllowedError/AbortError.
    if (['NotAllowedError', 'AbortError', 'NotFoundError'].includes(error?.name)) {
      throw new CaptureCancelled();
    }
    throw error;
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
}

/**
 * Where a rectangle drawn on the page lands inside the captured frame.
 *
 * The frame is not always the same thing: the picker lets the user hand us the
 * tab, the whole browser window, or a monitor, and each has a different origin
 * and a different scale. `displaySurface` on the track says which one arrived,
 * so the box the user drew in viewport coordinates is re-based accordingly:
 *
 *   browser  the frame IS the viewport      → viewport coords, scaled
 *   monitor  the frame is the whole screen  → screen coords, scaled
 *   window   the frame is the browser window, chrome included → viewport
 *            coords offset by the chrome above and beside them
 *
 * Pure, and exported, because this is the part that is easy to get subtly
 * wrong and impossible to eyeball in a screenshot afterwards.
 */
export function mapRegionToFrame(region, frame, surface = 'browser') {
  const bases = {
    browser: { rect: region.viewport, space: region.viewportSize },
    monitor: { rect: region.screen, space: region.screenSize },
    window: {
      rect: {
        ...region.viewport,
        x: region.viewport.x + Math.max(0, (region.windowSize.width - region.viewportSize.width) / 2),
        y: region.viewport.y + Math.max(0, region.windowSize.height - region.viewportSize.height),
      },
      space: region.windowSize,
    },
  };
  const { rect, space } = bases[surface] || bases.browser;

  // Scale from CSS pixels to frame pixels. Derived from the frame itself
  // rather than devicePixelRatio: the capture may be downscaled by the
  // browser (a 4K monitor often arrives at 1080p) and only the ratio between
  // what we measured and what we got is true.
  const scaleX = space.width ? frame.width / space.width : 1;
  const scaleY = space.height ? frame.height / space.height : 1;

  const x = Math.round(rect.x * scaleX);
  const y = Math.round(rect.y * scaleY);
  const width = Math.round(rect.width * scaleX);
  const height = Math.round(rect.height * scaleY);

  // Clamp: an approximate origin (window/monitor) can put an edge just past
  // the frame, and drawImage would then return transparent pixels there.
  const left = Math.min(Math.max(0, x), Math.max(0, frame.width - 1));
  const top = Math.min(Math.max(0, y), Math.max(0, frame.height - 1));
  return {
    x: left,
    y: top,
    width: Math.max(1, Math.min(width, frame.width - left)),
    height: Math.max(1, Math.min(height, frame.height - top)),
  };
}

/** Cut a region out of a captured frame into a canvas of its own. */
export function cropCanvas(source, region) {
  const canvas = document.createElement('canvas');
  canvas.width = region.width;
  canvas.height = region.height;
  canvas
    .getContext('2d')
    .drawImage(source, region.x, region.y, region.width, region.height, 0, 0, region.width, region.height);
  return canvas;
}

/**
 * Capture one frame and keep only the rectangle the user drew on the page.
 *
 * The crop is destructive on purpose: everything outside the box is discarded
 * here, in this tab, before the bitmap reaches the draft. The reporter drew a
 * box around one broken chart — the rest of their screen should not survive
 * that into an annotator they then have to remember to redact.
 */
export async function captureRegionFrame(region) {
  if (!isDisplayCaptureSupported()) throw new CaptureUnsupported();

  let stream = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      systemAudio: 'exclude',
    });
    const surface = stream.getVideoTracks()[0]?.getSettings?.().displaySurface || 'browser';
    const frame = await grabFrame(stream);
    return cropCanvas(frame, mapRegionToFrame(region, frame, surface));
  } catch (error) {
    if (['NotAllowedError', 'AbortError', 'NotFoundError'].includes(error?.name)) {
      throw new CaptureCancelled();
    }
    throw error;
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
  }
}

async function grabFrame(stream) {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  await video.play();
  // One rAF after `play()` resolves: the first frame is not necessarily
  // painted yet and a zero-sized video yields a blank canvas.
  await new Promise((resolve) => requestAnimationFrame(resolve));

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0);
  video.pause();
  video.srcObject = null;
  return canvas;
}

/** Load a pasted or picked file into a canvas. */
export async function canvasFromFile(file) {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return canvas;
}

/**
 * Paint the annotations onto the source pixels.
 *
 * `shapes` are in SOURCE pixel coordinates: `{ kind: 'highlight'|'redact',
 * x, y, width, height }`. `crop` is the same shape or null. `maxEdge` bounds
 * the output; the preview passes a smaller one than the upload.
 */
export function renderAnnotated(source, shapes = [], crop = null, maxEdge = MAX_EDGE_PX) {
  const region = crop || { x: 0, y: 0, width: source.width, height: source.height };
  const scale = Math.min(1, maxEdge / Math.max(region.width, region.height));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(region.width * scale));
  canvas.height = Math.max(1, Math.round(region.height * scale));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    source,
    region.x, region.y, region.width, region.height,
    0, 0, canvas.width, canvas.height
  );

  // Text last, so a note is never buried under a later highlight.
  const ordered = [...shapes].sort((a, b) => (a.kind === 'text' ? 1 : 0) - (b.kind === 'text' ? 1 : 0));
  const stroke = strokeWidthFor(canvas.height);

  ordered.forEach((shape) => {
    const x = (shape.x - region.x) * scale;
    const y = (shape.y - region.y) * scale;
    const width = shape.width * scale;
    const height = shape.height * scale;
    if (shape.kind === 'redact') {
      // Fully opaque, and painted before encoding — the pixels underneath are
      // gone, not merely covered by an overlay the viewer could peel off.
      // Deliberately NOT a theme colour: a redaction must read the same in
      // every theme, and a translucent token would defeat the point.
      ctx.fillStyle = 'black';
      ctx.globalAlpha = 1;
      ctx.fillRect(x, y, width, height);
    } else if (shape.kind === 'text') {
      drawTextBox(ctx, shape, { x, y, width, height }, scale);
    } else if (shape.kind === 'arrow') {
      ctx.strokeStyle = themeColor('--color-state-danger', 'crimson');
      ctx.fillStyle = ctx.strokeStyle;
      drawArrow(ctx, { x, y }, { x: x + width, y: y + height }, stroke);
    } else {
      ctx.strokeStyle = themeColor('--color-state-danger', 'crimson');
      ctx.lineWidth = stroke;
      ctx.strokeRect(x, y, width, height);
    }
  });

  return canvas;
}

/** Line weight that reads the same on a 900px capture and a 2600px one. */
export function strokeWidthFor(renderedHeight) {
  return Math.max(2, renderedHeight * STROKE_RATIO);
}

/** Shaft plus a filled head, pointing from `from` to `to`. */
function drawArrow(ctx, from, to, width) {
  const head = Math.max(10, width * 3.5);
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  // Stop the shaft short of the tip so it does not show through the head.
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x - Math.cos(angle) * head * 0.85, to.y - Math.sin(angle) * head * 0.85);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - Math.cos(angle - 0.42) * head, to.y - Math.sin(angle - 0.42) * head);
  ctx.lineTo(to.x - Math.cos(angle + 0.42) * head, to.y - Math.sin(angle + 0.42) * head);
  ctx.closePath();
  ctx.fill();
}

/**
 * A caption box: near-opaque panel, accent border, wrapped text.
 *
 * Near-opaque rather than a translucent wash on purpose — the point of a
 * caption is that it survives OCR and a re-screenshot, and text over a busy
 * chart does neither. The panel is painted with globalAlpha and the text at
 * full alpha, so the glyphs themselves stay crisp.
 */
function drawTextBox(ctx, shape, box, scale) {
  const sourceFont = shape.fontSize || 18;
  const fontSize = sourceFont * scale;
  const padding = textPadding(sourceFont) * scale;

  ctx.globalAlpha = 0.92;
  ctx.fillStyle = themeColor('--color-surface-base', 'white');
  ctx.fillRect(box.x, box.y, box.width, box.height);
  ctx.globalAlpha = 1;

  ctx.strokeStyle = themeColor('--color-state-danger', 'crimson');
  ctx.lineWidth = Math.max(1.5, fontSize * 0.09);
  ctx.strokeRect(box.x, box.y, box.width, box.height);

  const text = (shape.text || '').trim();
  if (!text) return;

  ctx.font = `${fontSize}px ${TEXT_FONT_STACK}`;
  ctx.fillStyle = themeColor('--color-text-primary', 'black');
  ctx.textBaseline = 'top';
  const lines = wrapLines(text, shape.width - textPadding(sourceFont) * 2, ctx, sourceFont);
  lines.forEach((line, index) => {
    ctx.fillText(line, box.x + padding, box.y + padding + index * fontSize * TEXT_LINE_HEIGHT);
  });
}

/**
 * Scratch context used only to measure text.
 *
 * `undefined` means "not probed yet"; a failed probe caches `null` so a
 * backend-less environment is asked once rather than on every wrap.
 */
let measureCtx;
function getMeasureContext() {
  if (measureCtx !== undefined) return measureCtx;
  try {
    measureCtx = document.createElement('canvas').getContext('2d') || null;
  } catch {
    measureCtx = null;
  }
  return measureCtx;
}

/**
 * Greedy word wrap in SOURCE pixels.
 *
 * Falls back to an average-character-width estimate when no 2D context is
 * available (jsdom), so layout maths stays testable without a canvas backend.
 */
export function wrapLines(text, maxWidth, ctx = null, fontPx = 18) {
  const context = ctx || getMeasureContext();
  const widthOf = (value) => {
    if (context && typeof context.measureText === 'function') {
      const measured = context.measureText(value);
      if (measured && typeof measured.width === 'number') return measured.width;
    }
    // ~0.55em is a decent average for a proportional UI face.
    return value.length * fontPx * 0.55;
  };
  if (context && !ctx) context.font = `${fontPx}px ${TEXT_FONT_STACK}`;

  const lines = [];
  String(text)
    .split('\n')
    .forEach((paragraph) => {
      let current = '';
      paragraph.split(/\s+/).forEach((word) => {
        if (!word) return;
        const candidate = current ? `${current} ${word}` : word;
        if (current && widthOf(candidate) > maxWidth) {
          lines.push(current);
          current = word;
        } else {
          current = candidate;
        }
      });
      lines.push(current);
    });
  return lines.length ? lines : [''];
}

/** Height a caption needs for its text at the given width, in source pixels. */
export function textBoxHeight(text, width, fontPx = 18) {
  const padding = textPadding(fontPx);
  const lines = wrapLines(text || '', Math.max(1, width - padding * 2), null, fontPx);
  return Math.round(padding * 2 + lines.length * fontPx * TEXT_LINE_HEIGHT);
}

/** Rect intersection, tolerant of the negative extents an arrow carries. */
function overlaps(a, b) {
  const box = (r) => ({
    left: Math.min(r.x, r.x + r.width),
    right: Math.max(r.x, r.x + r.width),
    top: Math.min(r.y, r.y + r.height),
    bottom: Math.max(r.y, r.y + r.height),
  });
  const first = box(a);
  const second = box(b);
  return (
    first.left < second.right &&
    first.right > second.left &&
    first.top < second.bottom &&
    first.bottom > second.top
  );
}

/**
 * Where to put the caption for a highlight so the screenshot stays readable.
 *
 * Tries below, above, right, then left of the anchor, taking the first
 * placement that fits inside the image and clears the marks already on it.
 * Falling back to "fits but overlaps" beats falling back to "off-image": a
 * caption nobody can see is worse than one that crowds another box.
 */
export function placeTextBox(anchor, region, existing = [], fontPx = 18) {
  const width = captionWidth(anchor, region);
  const height = textBoxHeight('', width, fontPx);
  const gap = Math.max(8, region.height * TEXT_GAP_RATIO);

  return chooseTextBox(
    [
      { x: anchor.x, y: anchor.y + anchor.height + gap },
      { x: anchor.x, y: anchor.y - height - gap },
      { x: anchor.x + anchor.width + gap, y: anchor.y },
      { x: anchor.x - width - gap, y: anchor.y },
    ],
    { region, existing, width, height, fontPx }
  );
}

/**
 * Where to put the caption for an arrow: at its TAIL, on the far side from
 * the head.
 *
 * An arrow already points at the thing. Putting its label at the head would
 * cover exactly what the reader is being pointed to, so the label goes behind
 * the arrow and the arrow leads away from it into the subject.
 */
export function placeTextBoxForArrow(arrow, region, existing = [], fontPx = 18) {
  const width = captionWidth(arrow, region);
  const height = textBoxHeight('', width, fontPx);
  const gap = Math.max(8, region.height * TEXT_GAP_RATIO);

  const tail = { x: arrow.x, y: arrow.y };
  const left = { x: tail.x - width - gap, y: tail.y - height / 2 };
  const right = { x: tail.x + gap, y: tail.y - height / 2 };
  const above = { x: tail.x - width / 2, y: tail.y - height - gap };
  const below = { x: tail.x - width / 2, y: tail.y + gap };

  // Behind the tail first, then the two perpendicular sides, then in front.
  const horizontal = Math.abs(arrow.width) >= Math.abs(arrow.height);
  const behind = horizontal ? (arrow.width >= 0 ? left : right) : arrow.height >= 0 ? above : below;
  const ahead = horizontal ? (arrow.width >= 0 ? right : left) : arrow.height >= 0 ? below : above;
  const sideways = horizontal ? [above, below] : [left, right];

  return chooseTextBox([behind, ...sideways, ahead], { region, existing, width, height, fontPx });
}

function captionWidth(anchor, region) {
  return Math.max(
    region.width * TEXT_MIN_WIDTH_RATIO,
    Math.min(Math.abs(anchor.width), region.width * 0.5)
  );
}

/**
 * Take the first candidate that is inside the image and clear of the marks
 * already on it; failing that the first that merely fits; failing that the
 * first one clamped into bounds. A caption nobody can see is worse than one
 * that crowds another box.
 */
function chooseTextBox(candidates, { region, existing, width, height, fontPx }) {
  const boxes = candidates.map((candidate) => ({ ...candidate, width, height }));

  const insideRegion = (box) =>
    box.x >= region.x &&
    box.y >= region.y &&
    box.x + box.width <= region.x + region.width &&
    box.y + box.height <= region.y + region.height;

  const clear = boxes.find(
    (box) => insideRegion(box) && !existing.some((shape) => overlaps(box, shape))
  );
  if (clear) return { kind: 'text', text: '', fontSize: fontPx, ...clear };

  const chosen = boxes.find(insideRegion) || boxes[0];
  return {
    kind: 'text',
    text: '',
    fontSize: fontPx,
    x: Math.min(Math.max(chosen.x, region.x), region.x + region.width - width),
    y: Math.min(Math.max(chosen.y, region.y), region.y + region.height - height),
    width,
    height,
  };
}

/** Resolve a CSS-token colour for canvas use; these pixels are baked into the
 *  uploaded image, so they read the token once at draw time. */
function themeColor(token, fallback) {
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value ? `rgb(${value})` : fallback;
  } catch {
    return fallback;
  }
}

function toBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Encode under the upload limit, degrading quality before dimensions.
 * Returns the first attempt that fits; the PNG fallback is returned even if
 * oversized so the caller can surface a real error rather than `null`.
 */
export async function encodeUnderLimit(canvas, maxBytes = MAX_UPLOAD_BYTES) {
  let last = null;
  for (const { type, quality } of ENCODE_ATTEMPTS) {
    const blob = await toBlob(canvas, type, quality);
    if (!blob) continue;
    last = blob;
    if (blob.size <= maxBytes) return blob;
  }
  return last;
}
